import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DataTable } from '../../components/ui/data-table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, FileText, Package, Database, Download, Upload } from 'lucide-react';

const CATEGORII = [
  'MECANICA', 'ELECTRICA', 'HIDRAULICA', 'PNEUMATICA',
  'LUBRIFIERE', 'CALIBRARE', 'SIGURANTA'
];

const FRECVENTE = [
  'ZILNIC', 'SAPTAMANAL', 'LUNAR', 'TRIMESTRIAL',
  'SEMESTRIAL', 'ANUAL'
];

const CATEGORIE_COLORS = {
  MECANICA: 'bg-blue-100 text-blue-700',
  ELECTRICA: 'bg-amber-100 text-amber-700',
  HIDRAULICA: 'bg-cyan-100 text-cyan-700',
  PNEUMATICA: 'bg-purple-100 text-purple-700',
  LUBRIFIERE: 'bg-emerald-100 text-emerald-700',
  CALIBRARE: 'bg-orange-100 text-orange-700',
  SIGURANTA: 'bg-red-100 text-red-700',
};

const emptyForm = {
  cod: '', denumire: '', categorie: '', frecventa: '',
  descriere: '', is_active: true
};

const PMProceduresPage = () => {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categorieFilter, setCategorieFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = { is_active: true };
      if (categorieFilter !== 'all') params.categorie = categorieFilter;
      const res = await api.get('/pm/procedures', { params });
      setItems(res.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [categorieFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cod: formData.cod.toUpperCase(),
        denumire: formData.denumire.toUpperCase(),
        categorie: formData.categorie.toUpperCase(),
        frecventa: formData.frecventa.toUpperCase(),
        descriere: formData.descriere.toUpperCase()
      };
      if (editItem) {
        await api.put(`/pm/procedures/${editItem.id}`, payload);
        toast.success('Procedura actualizata!');
      } else {
        await api.post('/pm/procedures', payload);
        toast.success('Procedura adaugata!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData(emptyForm);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      cod: item.cod || '',
      denumire: item.denumire || '',
      categorie: item.categorie || '',
      frecventa: item.frecventa || '',
      descriere: item.descriere || '',
      is_active: item.is_active !== false
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta procedura?')) return;
    try {
      await api.delete(`/pm/procedures/${id}`);
      toast.success('Procedura stearsa!');
      fetchItems();
    } catch {
      toast.error('Eroare la stergere');
    }
  };

  const handleSeed = async () => {
    try {
      const res = await api.post('/pm/procedures/seed');
      toast.success(res.data.message);
      fetchItems();
    } catch {
      toast.error('Eroare la populare nomenclator');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/export/pm-procedures', { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.setAttribute('download', 'proceduri_mentenanta.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export realizat cu succes!');
    } catch { toast.error('Eroare la export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/import/pm-procedures', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      fetchItems();
    } catch { toast.error('Eroare la import'); }
    e.target.value = '';
  };

  const filteredItems = items.filter(i => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (i.denumire || '').toLowerCase().includes(s) ||
           (i.cod || '').toLowerCase().includes(s) ||
           (i.descriere || '').toLowerCase().includes(s);
  });

  const tableColumns = [
    { key: 'cod', label: 'Cod', sortable: true, width: 'w-28',
      render: v => <span className="font-mono text-sm font-medium text-slate-700">{v}</span> },
    { key: 'denumire', label: 'Denumire procedura', sortable: true, filterable: true,
      render: v => <span className="font-medium">{v}</span> },
    { key: 'categorie', label: 'Categorie', sortable: true, filterable: true, width: 'w-32',
      render: v => <Badge className={CATEGORIE_COLORS[v] || 'bg-slate-100'}>{v}</Badge> },
    { key: 'frecventa', label: 'Frecventa', sortable: true, filterable: true, width: 'w-28',
      render: v => <Badge variant="outline" className="font-normal">{v}</Badge> },
    { key: 'descriere', label: 'Descriere', sortable: false,
      render: v => <span className="text-slate-600 text-xs">{v ? (v.length > 80 ? v.substring(0, 80) + '...' : v) : '-'}</span> },
    { key: 'actions', label: 'Actiuni', sortable: false, width: 'w-24',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {hasRole('admin', 'manager') && (
            <>
              <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} className="h-8 w-8 p-0">
                <Edit className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="h-8 w-8 p-0 text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      )
    }
  ];

  // Stats per category
  const categoryCounts = {};
  items.forEach(i => {
    const cat = i.categorie || 'NECUNOSCUT';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  return (
    <div className="space-y-5" data-testid="pm-procedures-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Proceduri Mentenanta Preventiva</h1>
          <p className="text-slate-500 mt-1">Nomenclator proceduri standardizate de mentenanta</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-10" onClick={handleExport} data-testid="pm-procedures-export-btn">
            <Download className="w-4 h-4 mr-1" />Export Excel
          </Button>
          <Button variant="outline" className="h-10" onClick={() => fileInputRef.current?.click()} data-testid="pm-procedures-import-btn">
            <Upload className="w-4 h-4 mr-1" />Import Excel
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          {hasRole('admin', 'manager') && (
            <Button className="h-10 bg-[#0052CC] hover:bg-[#003d99]"
                    onClick={() => { setEditItem(null); setFormData(emptyForm); setShowDialog(true); }}
                    data-testid="pm-add-procedure-btn">
              <Plus className="w-4 h-4 mr-1" />ADAUGA PROCEDURA
            </Button>
          )}
        </div>
      </div>

      {/* Category stats */}
      <div className="flex flex-wrap gap-2">
        {CATEGORII.map(cat => (
          <button key={cat}
                  onClick={() => setCategorieFilter(categorieFilter === cat ? 'all' : cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    categorieFilter === cat
                      ? 'ring-2 ring-offset-1 ring-blue-400 ' + (CATEGORIE_COLORS[cat] || '')
                      : CATEGORIE_COLORS[cat] || 'bg-slate-100'
                  }`}>
            {cat} ({categoryCounts[cat] || 0})
          </button>
        ))}
        {categorieFilter !== 'all' && (
          <button onClick={() => setCategorieFilter('all')}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
            TOATE ({items.length})
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cauta procedura..." value={searchTerm}
               onChange={e => setSearchTerm(e.target.value)} className="pl-10 h-10" />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : filteredItems.length > 0 ? (
        <DataTable
          data={filteredItems}
          columns={tableColumns}
          pageSize={15}
          searchable={false}
          exportable={true}
          printable={true}
          emptyMessage="Nu exista proceduri"
        />
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-14 h-14 text-slate-300 mb-3" />
            <p className="text-slate-500 text-lg">Nomenclatorul de proceduri este gol</p>
            <p className="text-slate-400 text-sm mt-1 mb-4">Populati cu proceduri profesionale predefinite</p>
            {hasRole('admin', 'manager') && (
              <Button onClick={handleSeed} data-testid="pm-seed-procedures-btn">
                <Database className="w-4 h-4 mr-1" />Populeaza nomenclator (33 proceduri)
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Editeaza procedura' : 'Adauga procedura noua'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cod procedura *</Label>
                <Input value={formData.cod} className="h-11" required
                       onChange={e => setFormData(p => ({ ...p, cod: e.target.value.toUpperCase() }))}
                       placeholder="ex: PM-MEC-001" data-testid="pm-proc-cod" />
              </div>
              <div className="space-y-2">
                <Label>Categorie *</Label>
                <Select value={formData.categorie} onValueChange={v => setFormData(p => ({ ...p, categorie: v }))}>
                  <SelectTrigger className="h-11" data-testid="pm-proc-categorie">
                    <SelectValue placeholder="Selecteaza" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORII.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Denumire procedura *</Label>
              <Input value={formData.denumire} className="h-11" required
                     onChange={e => setFormData(p => ({ ...p, denumire: e.target.value.toUpperCase() }))}
                     placeholder="ex: VERIFICARE SISTEM HIDRAULIC" data-testid="pm-proc-denumire" />
            </div>
            <div className="space-y-2">
              <Label>Frecventa</Label>
              <Select value={formData.frecventa} onValueChange={v => setFormData(p => ({ ...p, frecventa: v }))}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Selecteaza frecventa" />
                </SelectTrigger>
                <SelectContent>
                  {FRECVENTE.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descriere</Label>
              <Textarea value={formData.descriere} rows={3}
                        onChange={e => setFormData(p => ({ ...p, descriere: e.target.value.toUpperCase() }))}
                        placeholder="Descrierea detaliata a procedurii de mentenanta..." />
            </div>
            <Button type="submit" className="w-full h-11 bg-[#0052CC]" data-testid="pm-proc-save-btn">
              {editItem ? 'ACTUALIZEAZA' : 'ADAUGA PROCEDURA'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PMProceduresPage;
